import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './SalesOrderReport.css';

// ─── Must match AdminOrderDetails exactly ────────────────────────────────────
const SHIRT_SIZE_PRICES = {
    'Blue Corner White T-Shirt':        { xs:150, small:150, medium:150, large:160, xl:165, xxl:170, xxxl:175, xxxxl:180, xxxxxl:200 },
    'Blue Corner Colored T-Shirt':      { xs:180, small:180, medium:180, large:195, xl:195, xxl:205, xxxl:210, xxxxl:220, xxxxxl:235 },
    'Yalex White T-Shirt':              { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Yalex Colored T-Shirt':            { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Pro Club Inspire White T-Shirt':   { xs:null, small:200, medium:200, large:200, xl:220, xxl:230, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire Colored T-Shirt': { xs:null, small:250, medium:250, large:250, xl:260, xxl:270, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse White T-Shirt':         { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse Colored T-Shirt':       { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:null, xxxxl:null, xxxxxl:null },
    'Active Life White T-Shirt':        { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Active Life Colored T-Shirt':      { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Drift White T-Shirt':              { xs:85,  small:85,  medium:85,  large:85,  xl:90,  xxl:95,  xxxl:null, xxxxl:null, xxxxxl:null },
    'Drift Colored T-Shirt':            { xs:95,  small:95,  medium:95,  large:95,  xl:105, xxl:115, xxxl:null, xxxxl:null, xxxxxl:null },
};
const IMPRINT_RECT = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_CIRC = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

function calcShirtSubtotal(item) {
    const quantities = item.quantities || {};
    const sizePrices = SHIRT_SIZE_PRICES[item.type] || {};
    return Object.entries(quantities).reduce((s, [sz, q]) => s + (q || 0) * (sizePrices[sz] || 0), 0);
}
function calcPrintSubtotal(item) {
    const quantities   = item.quantities || {};
    const totalQty     = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    const shape        = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table        = shape === 'circle' ? IMPRINT_CIRC : IMPRINT_RECT;
    const locs         = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices       = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);
    return imprintPerPc * totalQty;
}
function calcOrderTotal(order) {
    const items = Array.isArray(order.orderItems) ? order.orderItems : [];
    return items.reduce((s, item) => s + calcShirtSubtotal(item) + calcPrintSubtotal(item), 0);
}
function getOrderDiscount(order) {
    return Number(order?.discountAmount || 0);
}
function getOrderFinalAmount(order) {
    const deliveryFee = Number(order?.deliveryFee || 0);
    return Math.max(0, calcOrderTotal(order) + deliveryFee - getOrderDiscount(order));
}
function getTotalQty(order) {
    const items = Array.isArray(order.orderItems) ? order.orderItems : [];
    return items.reduce((s, item) => {
        return s + Object.values(item.quantities || {}).reduce((a, q) => a + (q || 0), 0);
    }, 0);
}
function getProductNames(order) {
    const items = Array.isArray(order.orderItems) ? order.orderItems : [];
    if (items.length === 0) return '—';
    const names = [...new Set(items.map(i => i.type || i.name).filter(Boolean))];
    return names.join(', ') || '—';
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_TABS = [
    { key: 'all',              label: 'All Orders'        },
    { key: 'pending_approval', label: 'Pending Approval'  },
    { key: 'approved',         label: 'Approved'          },
    { key: 'awaiting_payment', label: 'Awaiting Payment'  },
    { key: 'in_production',    label: 'In Production'     },
    { key: 'to_be_delivered',  label: 'To Be Delivered'   },
    { key: 'delivered',        label: 'Delivered'         },
    { key: 'cancelled',        label: 'Cancelled'         },
];

const STATUS_DISPLAY = {
    pending:            { label: 'Pending Approval', cls: 'badge--yellow' },
    pending_approval:   { label: 'Pending Approval', cls: 'badge--yellow' },
    approved:           { label: 'Approved',         cls: 'badge--blue'   },
    awaiting_payment:   { label: 'Awaiting Payment', cls: 'badge--purple' },
    in_production:      { label: 'In Production',    cls: 'badge--green'  },
    processing:         { label: 'In Production',    cls: 'badge--green'  },
    'in production':    { label: 'In Production',    cls: 'badge--green'  },
    to_be_delivered:    { label: 'To Be Delivered',  cls: 'badge--orange' },
    'to deliver':       { label: 'To Be Delivered',  cls: 'badge--orange' },
    delivered:          { label: 'Delivered',        cls: 'badge--gray'   },
    completed:          { label: 'Delivered',        cls: 'badge--gray'   },
    cancelled:          { label: 'Cancelled',        cls: 'badge--red'    },
};

const DATE_OPTIONS = [
    { value: 'all',   label: 'All Time'   },
    { value: 'today', label: 'Today'      },
    { value: 'week',  label: 'This Week'  },
    { value: 'month', label: 'This Month' },
    { value: 'year',  label: 'This Year'  },
];

function getDateStart(filter) {
    const now = new Date();
    switch (filter) {
        case 'today': { const d = new Date(now); d.setHours(0,0,0,0); return d; }
        case 'week':  { const d = new Date(now); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d; }
        case 'month': return new Date(now.getFullYear(), now.getMonth(), 1);
        case 'year':  return new Date(now.getFullYear(), 0, 1);
        default:      return null;
    }
}

function getOrderDate(order) {
    if (!order) return null;
    if (order.createdAt?.toDate) return order.createdAt.toDate();
    if (order.createdAt?.seconds) return new Date(order.createdAt.seconds * 1000);
    if (order.date) return new Date(order.date);
    return null;
}

function getDeliveredDate(order) {
    const statusHistory = Array.isArray(order?.statusHistory) ? order.statusHistory : [];
    const deliveredEntry = [...statusHistory].reverse().find((entry) => normaliseStatus(entry?.status) === 'delivered');
    if (deliveredEntry?.timestamp) {
        return new Date(deliveredEntry.timestamp);
    }
    if (order?.updatedAt?.toDate) return order.updatedAt.toDate();
    if (order?.updatedAt?.seconds) return new Date(order.updatedAt.seconds * 1000);
    return getOrderDate(order);
}

function formatDate(order) {
    const d = getOrderDate(order);
    if (!d || isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(amount) {
    if (amount == null) return '—';
    return `₱ ${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
}

function normaliseStatus(status) {
    return (status || '').toLowerCase().replace(/\s+/g, '_');
}

export default function SalesOrderReport() {
    const [activeTab,  setActiveTab]  = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [allOrders,  setAllOrders]  = useState([]);
    const [loading,    setLoading]    = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, 'orders'));
                const data = snap.docs.map(d => ({ id: d.id, firestoreId: d.id, ...d.data() }));
                setAllOrders(data);
            } catch (err) {
                console.error('Error fetching orders:', err);
                setAllOrders([]);
            }
            setLoading(false);
        })();
    }, []);

    // ── Helper: apply only date filter (used for stat cards) ─────────────────
    // Stat cards always reflect the full picture for the selected date range,
    // regardless of which status tab is active.
    const dateFilteredOrders = allOrders.filter(o => {
        const dateStart = getDateStart(dateFilter);
        if (dateStart) {
            const d = getOrderDate(o);
            if (!d || d < dateStart) return false;
        }
        return true;
    });

    // ── Helper: apply BOTH date + status filter (used for table only) ─────────
    const tableOrders = dateFilteredOrders.filter(o => {
        if (activeTab === 'all') return true;
        return normaliseStatus(o.status) === activeTab;
    });

    // ── Stat card metrics — always from dateFilteredOrders (no status filter) ─
    const totalOrders    = dateFilteredOrders.length;
    const deliveredCount = dateFilteredOrders.filter(o =>
        ['delivered', 'completed'].includes(normaliseStatus(o.status))
    ).length;
    const pendingCount   = dateFilteredOrders.filter(o =>
        ['pending', 'pending_approval'].includes(normaliseStatus(o.status))
    ).length;
    const revenue        = dateFilteredOrders
        .filter(o => normaliseStatus(o.status) !== 'cancelled')
        .reduce((sum, o) => sum + calcOrderTotal(o), 0);

    // ── Export uses tableOrders (what the admin currently sees) ──────────────
    function handleExport() {
        const rows = tableOrders.map(o => ({
            'Order ID':     o.orderId || o.id,
            'Customer Name': o.customerName || '—',
            'Products':     getProductNames(o),
            'Total Qty':    getTotalQty(o),
            'Total Amount': getOrderFinalAmount(o),
            'Order Date':   formatDate(o),
            'Status':       STATUS_DISPLAY[normaliseStatus(o.status)]?.label || o.status || '—',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');
        XLSX.writeFile(wb, `JG_Sales_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    }

    function handleDeliveredExport() {
        const deliveredOrders = dateFilteredOrders.filter((order) => ['delivered', 'completed'].includes(normaliseStatus(order.status)));
        const rows = deliveredOrders.flatMap((order) => {
            const items = Array.isArray(order.orderItems) ? order.orderItems : [];
            const discountAmount = getOrderDiscount(order);
            const finalAmount = getOrderFinalAmount(order);
            const completedDate = getDeliveredDate(order);
            const completedDateLabel = completedDate && !Number.isNaN(completedDate.getTime())
                ? completedDate.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
                : 'N/A';

            return items.map((item) => ({
                'Item Name': item.name || item.type || 'N/A',
                'Price Discount': discountAmount,
                'Final Amount': finalAmount,
                'Transaction Completed Date': completedDateLabel,
            }));
        });

        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{
            'Item Name': 'N/A',
            'Price Discount': 0,
            'Final Amount': 0,
            'Transaction Completed Date': 'N/A',
        }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Delivered Orders');
        XLSX.writeFile(wb, `JG_Delivered_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
    }

    return (
        <div className="report-section">

            {/* ── Header ── */}
            <div className="report-header">
                <h2 className="report-title">Sales &amp; Order Report</h2>
                <div className="report-actions">
                    <select
                        className="report-date-select"
                        value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                    >
                        {DATE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <button className="report-export-btn" onClick={handleExport} title="Export to Excel">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                    </button>
                    <button className="report-export-btn--text" onClick={handleDeliveredExport} title="Export delivered orders to Excel">
                        Delivered
                    </button>
                </div>
            </div>

            {/* ── Stat Cards — always global (date filter only, no status filter) ── */}
            <div className="report-stat-cards">
                <div className="stat-card stat-card--green">
                    <span className="stat-card__label">Total Orders</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                    </svg>
                    <span className="stat-card__value">{totalOrders}</span>
                </div>
                <div className="stat-card stat-card--blue">
                    <span className="stat-card__label">Delivered</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                    </svg>
                    <span className="stat-card__value">{deliveredCount}</span>
                </div>
                <div className="stat-card stat-card--yellow">
                    <span className="stat-card__label">Pending Approval</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                    </svg>
                    <span className="stat-card__value">{pendingCount}</span>
                </div>
                <div className="stat-card stat-card--green">
                    <span className="stat-card__label">Revenue</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 20V10M12 20V4M6 20v-6"/>
                    </svg>
                    <span className="stat-card__value stat-card__value--currency">{formatCurrency(revenue)}</span>
                </div>
            </div>

            {/* ── Status Tabs — filters the table only ── */}
            <div className="report-filter-tabs">
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`report-filter-tab${activeTab === tab.key ? ' is-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Table — uses tableOrders (date + status filtered) ── */}
            <div className="report-table-wrapper">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Customer</th>
                            <th>Products</th>
                            <th>Qty</th>
                            <th>Total Amount</th>
                            <th>Order Date</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="report-table__empty">Loading…</td></tr>
                        ) : tableOrders.length === 0 ? (
                            <tr><td colSpan={7} className="report-table__empty">No orders found.</td></tr>
                        ) : (
                            tableOrders.map(order => (
                                <tr key={order.id}>
                                    <td className="report-table__id">{order.orderId || `#${order.id.slice(0,8)}`}</td>
                                    <td>{order.customerName || '—'}</td>
                                    <td>{getProductNames(order)}</td>
                                    <td>{getTotalQty(order)}</td>
                                    <td>{formatCurrency(calcOrderTotal(order))}</td>
                                    <td>{formatDate(order)}</td>
                                    <td><StatusBadge status={order.status} /></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {!loading && tableOrders.length > 0 && (
                <p className="report-result-count">
                    Showing {tableOrders.length} order{tableOrders.length !== 1 ? 's' : ''}
                    {activeTab !== 'all' && ` (${tableOrders.length} of ${totalOrders} total)`}
                </p>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const norm = normaliseStatus(status);
    const info = STATUS_DISPLAY[norm] || { label: status || '—', cls: 'badge--gray' };
    return <span className={`status-badge ${info.cls}`}>{info.label}</span>;
}